import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Search } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { PageHeader } from "../../components/ui/page-header";
import { useAdminUsers } from "../../api/hooks";
import { useDebouncedValue } from "../../lib/useDebouncedValue";

/**
 * Admin: paginated list of every user with a name/email search. Each row links
 * to the per-user detail screen. Gated behind {@link AdminRoute}; the API is
 * the real boundary.
 */
export function AdminUsersPage(): ReactNode {
  const [search, setSearch] = useState("");
  const [includeTestAccounts, setIncludeTestAccounts] = useState(false);
  const debounced = useDebouncedValue(search, 300);
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAdminUsers(debounced, { includeTestAccounts });

  const users = data?.pages.flatMap((p) => p.items) ?? [];

  // The `q` filter is applied AFTER each DynamoDB scan page, so an early page
  // can return zero matches while later pages still hold some. Auto-advance
  // through those empty pages so a search doesn't falsely read as "no match"
  // until the cursor is genuinely exhausted (the empty state below is gated on
  // !hasNextPage for the same reason).
  useEffect(() => {
    if (users.length === 0 && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [users.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Still scanning for matches across remaining pages.
  const searching = users.length === 0 && (isFetchingNextPage || hasNextPage);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Admin"
        title="Users"
        description="Search and inspect user accounts, credits, and group membership."
      />

      <div className="space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-on-surface-variant" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email"
            className="pl-9"
            aria-label="Search users"
          />
        </div>
        {/*
          Integration-test accounts are hidden by default (issue #451) — the
          suite's teardown is best-effort, so a cancelled run leaves them
          behind. The toggle is here for auditing those leftovers.
        */}
        <label className="flex w-fit cursor-pointer items-center gap-2 text-label-md text-on-surface-variant">
          <input
            type="checkbox"
            checked={includeTestAccounts}
            onChange={(e) => setIncludeTestAccounts(e.target.checked)}
            className="size-4 cursor-pointer accent-primary"
          />
          Show test accounts
        </label>
      </div>

      {isError ? (
        <Card className="p-6 text-body-md text-error">
          Couldn&apos;t load users. You may not have admin access.
        </Card>
      ) : isLoading || searching ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : users.length === 0 ? (
        <Card className="p-6 text-body-md text-on-surface-variant">
          {debounced ? `No users match “${debounced}”.` : "No users found."}
        </Card>
      ) : (
        <ul className="space-y-2">
          {users.map((user) => (
            <li key={user.id}>
              <Link
                to={`/admin/users/${user.id}`}
                className="flex items-center justify-between gap-4 rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-4 py-3 transition-colors hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <div className="min-w-0">
                  <p className="flex min-w-0 items-center gap-2 text-label-lg text-on-surface">
                    <span className="truncate">{user.displayName}</span>
                    {user.isTestAccount ? (
                      <span className="shrink-0 rounded-full bg-surface-container px-2 py-0.5 text-label-sm text-on-surface-variant">
                        Test
                      </span>
                    ) : null}
                  </p>
                  <p className="truncate text-body-sm text-on-surface-variant">
                    {user.email || "—"}
                  </p>
                </div>
                <ChevronRight className="size-5 shrink-0 text-on-surface-variant" />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {hasNextPage ? (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
